/**
 * Reports Page
 *
 * Template-based report generation interface.
 * Users select a template, choose scope, and generate PDF reports.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  LinearProgress,
  Alert,
  Chip,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Warning as WarningIcon,
  VerifiedUser as VerifiedUserIcon,
  Inventory as InventoryIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Assessment as ReportsIcon,
} from '@mui/icons-material';
import type {
  ReportTemplate,
  ReportScopeType,
  ReportGenerationRequest,
} from 'shared';
import { useSpace } from '../contexts/SpaceContext';
import { getTemplates, downloadReport } from '../services/reports.service';
import diagramsService from '../services/diagrams.service';
import { componentsService } from '../services/components.service';

// =============================================================================
// TYPES
// =============================================================================

interface ScopeOption {
  type: ReportScopeType;
  id: string;
  name: string;
}

type GenerationStep =
  | 'idle'
  | 'collecting'
  | 'generating'
  | 'rendering'
  | 'complete'
  | 'error';

// =============================================================================
// ICON MAPPING
// =============================================================================

const iconMap: Record<string, React.ReactElement> = {
  Security: <SecurityIcon />,
  Warning: <WarningIcon />,
  VerifiedUser: <VerifiedUserIcon />,
  Inventory: <InventoryIcon />,
};

const getCategoryColor = (category: string): string => {
  switch (category) {
    case 'security':
      return '#2D3B45'; // steel
    case 'compliance':
      return '#00D9FF'; // cyan
    case 'overview':
      return '#FFB300'; // amber
    default:
      return '#9AA0A6'; // slate
  }
};

// =============================================================================
// COMPONENT
// =============================================================================

export function ReportsPage() {
  const { t } = useTranslation();
  const { currentSpace } = useSpace();

  // State
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog state
  const [selectedTemplate, setSelectedTemplate] =
    useState<ReportTemplate | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Scope options
  const [scopeOptions, setScopeOptions] = useState<ScopeOption[]>([]);
  const [selectedScope, setSelectedScope] = useState<ScopeOption | null>(null);
  const [loadingScopes, setLoadingScopes] = useState(false);

  // Generation options
  const [includeAiSummary, setIncludeAiSummary] = useState(true);
  const [refreshAiCache, setRefreshAiCache] = useState(false);
  const [language, setLanguage] = useState<'en' | 'nb'>('nb');

  // Generation progress
  const [generationStep, setGenerationStep] = useState<GenerationStep>('idle');
  const [generationError, setGenerationError] = useState<string | null>(null);

  // Load templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoading(true);
        const data = await getTemplates();
        setTemplates(data);
      } catch (err) {
        setError(t('reports.loadError'));
        console.error('Failed to load templates:', err);
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, [t]);

  // Load scope options when dialog opens
  const loadScopeOptions = useCallback(
    async (template: ReportTemplate) => {
      if (!currentSpace) return;

      setLoadingScopes(true);
      const options: ScopeOption[] = [];

      try {
        // Always add space as an option
        if (template.requiredScopes.includes('space')) {
          options.push({
            type: 'space',
            id: currentSpace.id,
            name: currentSpace.name,
          });
        }

        // Add diagrams if supported
        if (template.requiredScopes.includes('diagram')) {
          const diagrams = await diagramsService.getAll(currentSpace.id);
          for (const diagram of diagrams) {
            options.push({
              type: 'diagram',
              id: diagram.id,
              name: diagram.name,
            });
          }
        }

        // Add components if supported
        if (template.requiredScopes.includes('component')) {
          const components = await componentsService.getBySpace(
            currentSpace.id
          );
          for (const component of components) {
            options.push({
              type: 'component',
              id: component.id,
              name: component.name,
            });
          }
        }

        setScopeOptions(options);

        // Auto-select first option
        if (options.length > 0) {
          setSelectedScope(options[0]);
        }
      } catch (err) {
        console.error('Failed to load scope options:', err);
      } finally {
        setLoadingScopes(false);
      }
    },
    [currentSpace]
  );

  // Handle template selection
  const handleSelectTemplate = (template: ReportTemplate) => {
    setSelectedTemplate(template);
    setDialogOpen(true);
    setGenerationStep('idle');
    setGenerationError(null);
    loadScopeOptions(template);
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTemplate(null);
    setSelectedScope(null);
    setScopeOptions([]);
    setGenerationStep('idle');
    setGenerationError(null);
  };

  // Handle report generation
  const handleGenerate = async () => {
    if (!selectedTemplate || !selectedScope) return;

    try {
      setGenerationStep('collecting');
      setGenerationError(null);

      // Simulate progress steps
      globalThis.setTimeout(() => setGenerationStep('generating'), 500);
      globalThis.setTimeout(() => setGenerationStep('rendering'), 1500);

      const request: ReportGenerationRequest = {
        templateId: selectedTemplate.id,
        scope: {
          type: selectedScope.type,
          id: selectedScope.id,
          name: selectedScope.name,
        },
        language,
        includeAiSummary,
        refreshAiCache,
      };

      await downloadReport(request);

      setGenerationStep('complete');

      // Close dialog after a short delay
      globalThis.setTimeout(() => {
        handleCloseDialog();
      }, 1500);
    } catch (err) {
      setGenerationStep('error');
      setGenerationError(
        err instanceof Error ? err.message : t('reports.generateError')
      );
    }
  };

  // Get progress percentage
  const getProgressPercent = (): number => {
    switch (generationStep) {
      case 'collecting':
        return 25;
      case 'generating':
        return 50;
      case 'rendering':
        return 75;
      case 'complete':
        return 100;
      default:
        return 0;
    }
  };

  // Get progress message
  const getProgressMessage = (): string => {
    switch (generationStep) {
      case 'collecting':
        return t('reports.progress.collecting');
      case 'generating':
        return t('reports.progress.generating');
      case 'rendering':
        return t('reports.progress.rendering');
      case 'complete':
        return t('reports.progress.complete');
      case 'error':
        return t('reports.progress.error');
      default:
        return '';
    }
  };

  // Render loading state
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Render no space selected
  if (!currentSpace) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">{t('reports.selectSpaceFirst')}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <ReportsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4">{t('reports.title')}</Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          {t('reports.description')}
        </Typography>
      </Box>

      {/* Template Cards */}
      <Grid container spacing={3}>
        {templates.map(template => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={template.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: `${getCategoryColor(template.category)}20`,
                      color: getCategoryColor(template.category),
                      mr: 2,
                    }}
                  >
                    {iconMap[template.icon] || <SecurityIcon />}
                  </Box>
                  <Box>
                    <Typography variant="h6" component="div">
                      {t(template.nameKey)}
                    </Typography>
                    <Chip
                      label={t(`reports.category.${template.category}`)}
                      size="small"
                      sx={{
                        bgcolor: `${getCategoryColor(template.category)}20`,
                        color: getCategoryColor(template.category),
                      }}
                    />
                  </Box>
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {t(template.descriptionKey)}
                </Typography>

                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {template.requiredScopes.map(scope => (
                    <Chip
                      key={scope}
                      label={t(`reports.scope.${scope}`)}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </CardContent>

              <CardActions>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleSelectTemplate(template)}
                  fullWidth
                >
                  {t('reports.generate')}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Generation Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={
          generationStep !== 'idle' && generationStep !== 'error'
        }
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6">
              {selectedTemplate
                ? t(selectedTemplate.nameKey)
                : t('reports.generate')}
            </Typography>
            <IconButton
              onClick={handleCloseDialog}
              disabled={
                generationStep !== 'idle' &&
                generationStep !== 'error' &&
                generationStep !== 'complete'
              }
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {/* Generation Progress */}
          {generationStep !== 'idle' && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 3,
                bgcolor:
                  generationStep === 'error' ? 'error.light' : 'primary.light',
                color:
                  generationStep === 'error'
                    ? 'error.contrastText'
                    : 'primary.contrastText',
              }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                {getProgressMessage()}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={getProgressPercent()}
                color={generationStep === 'error' ? 'error' : 'primary'}
              />
              {generationError && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {generationError}
                </Typography>
              )}
            </Paper>
          )}

          {/* Scope Selection */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>{t('reports.selectScope')}</InputLabel>
            <Select
              value={selectedScope?.id || ''}
              onChange={e => {
                const option = scopeOptions.find(o => o.id === e.target.value);
                setSelectedScope(option || null);
              }}
              label={t('reports.selectScope')}
              disabled={loadingScopes || generationStep !== 'idle'}
            >
              {scopeOptions.map(option => (
                <MenuItem key={option.id} value={option.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={t(`reports.scope.${option.type}`)}
                      size="small"
                      sx={{ minWidth: 80 }}
                    />
                    {option.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Language Selection */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>{t('reports.language')}</InputLabel>
            <Select
              value={language}
              onChange={e => setLanguage(e.target.value as 'en' | 'nb')}
              label={t('reports.language')}
              disabled={generationStep !== 'idle'}
            >
              <MenuItem value="nb">Norsk</MenuItem>
              <MenuItem value="en">English</MenuItem>
            </Select>
          </FormControl>

          {/* AI Summary Options */}
          {selectedTemplate?.aiPrompt && (
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeAiSummary}
                    onChange={e => setIncludeAiSummary(e.target.checked)}
                    disabled={generationStep !== 'idle'}
                  />
                }
                label={t('reports.includeAiSummary')}
              />

              {includeAiSummary && (
                <Box sx={{ ml: 4 }}>
                  <Tooltip title={t('reports.refreshCacheTooltip')}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={refreshAiCache}
                          onChange={e => setRefreshAiCache(e.target.checked)}
                          disabled={generationStep !== 'idle'}
                          size="small"
                        />
                      }
                      label={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <RefreshIcon fontSize="small" />
                          {t('reports.refreshCache')}
                        </Box>
                      }
                    />
                  </Tooltip>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            disabled={
              generationStep !== 'idle' &&
              generationStep !== 'error' &&
              generationStep !== 'complete'
            }
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={!selectedScope || generationStep !== 'idle'}
            startIcon={<DownloadIcon />}
          >
            {t('reports.generatePdf')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ReportsPage;
